import { useMutation } from '@tanstack/react-query'
import { assert } from '@/utils'
import { CHAIN_CONFIG, CHAIN_INFO_LIST } from '@/config'
import { calculateFeeWithMinimalDenom, signIbcTransaction2 } from '@/wallet-utils'
import { IbcReturnType, generateIbcTimeoutTimestampBigInt, simulateIbcTransaction2 } from '@/wallet-utils/ibc'
import { MutationParameters } from './types'
import { ibc } from 'stridejs'
import { useSelectedDenom } from '../../../../contexts/denom'
import { useSelectedChain, useStrideChain } from '@/contexts/wallet'

export interface SignIbcTransferMutationParameters {
  amount: string
}

const useSignIbcTransferMutation = ({ setIbcTransferRaw }: MutationParameters) => {
  const { address: strideAddress } = useStrideChain()

  const {
    address: selectedAddress,
    getSigningStargateClient,
    getOfflineSigner,
    getAccount,
    wallet
  } = useSelectedChain()

  const [denom] = useSelectedDenom()

  const handleMutation = async ({ amount }: SignIbcTransferMutationParameters): Promise<IbcReturnType> => {
    assert(strideAddress, 'You are unable to send token without connecting your wallet.')

    assert(selectedAddress, `You are unable to send token without connecting your ${denom} wallet.`)

    assert(wallet, `You are unable to send token without your ${denom} wallet instance.`)

    const transfer = ibc.applications.transfer.v1.MessageComposer.withTypeUrl.transfer({
      sourcePort: 'transfer',
      sourceChannel: CHAIN_CONFIG[denom].depositChannel,
      token: {
        amount,
        denom: CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom
      },
      sender: selectedAddress,
      receiver: strideAddress,
      memo: '',
      timeoutHeight: {
        revisionHeight: 0n,
        revisionNumber: 0n
      },
      timeoutTimestamp: generateIbcTimeoutTimestampBigInt()
    })

    const [client, account] = await Promise.all([getSigningStargateClient(), getAccount()])
    const ibcParams = { client, denom, account, offlineSigner: getOfflineSigner(), wallet: wallet.name }
    const gas = await simulateIbcTransaction2(ibcParams, selectedAddress, [transfer], '')
    const fee = calculateFeeWithMinimalDenom(CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom, gas)

    return await signIbcTransaction2(ibcParams, selectedAddress!, [transfer], fee, '')
  }

  const handleSuccess = (raw: IbcReturnType) => {
    setIbcTransferRaw(raw)
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useSignIbcTransferMutation }
